import React, { useCallback, useEffect, useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import {
  mapAssociatedPortServiceToOptions,
  mapMovementReasonsToOptions,
  mapVesselConditionsToOptions,
  type MovementReason,
  type VesselCondition,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import type { MovementOutletContext } from "@features/vessel-call/vessel-call-status/MovementServices/MovementServicesContainer";
import { useMovementServiceFormStore } from "@features/vessel-call/vessel-call-status/MovementServices/store/useMovementServiceForm.store.ts";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { getAssociatedPortServiceDetailsById } from "@services/movementServicesApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { useQuery } from "@tanstack/react-query";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import type { MovementServiceFormData } from "../schema/movementServiceFormSchema";
import { shouldShowVesselDetailRadioButtons } from "./movementVesselDetails.helpers";

const LANG_BASE = "vessel-call.movement-services";
const DEPARTED_VESSEL_CALL_STATUS = "DEPARTED";
const REINSTATEMENT_MOVEMENT_REASON = "Reinstatement";

type MovementVesselDetailsProps = {
  hasClosedSeaPastMovement: boolean;
  vesselCallId: string;
};

const MovementVesselDetails: React.FC<MovementVesselDetailsProps> = ({
  hasClosedSeaPastMovement,
  vesselCallId,
}) => {
  const { formData, updateFormData, vesselCallStatus } = useOutletContext<MovementOutletContext>();
  const { t } = useTranslation();
  const isDepartedVesselCall = vesselCallStatus === DEPARTED_VESSEL_CALL_STATUS;

  const { options: vesselConditions } = useFormattedReference<VesselCondition>(
    REFERENCE_DATA_TYPES.VESSEL_CONDITIONS,
    "bff-vessel-call",
  );

  const { options: movementReasons } = useFormattedReference<MovementReason>(
    REFERENCE_DATA_TYPES.MOVEMENT_REASONS,
    "bff-vessel-call",
  );

  const vesselConditionOptions = useMemo(
    () => mapVesselConditionsToOptions(vesselConditions),
    [vesselConditions],
  );

  const movementReasonOptions = useMemo(() => {
    const mappedMovementReasons = mapMovementReasonsToOptions(movementReasons);

    if (
      !isDepartedVesselCall ||
      mappedMovementReasons.some((option) => option.value === REINSTATEMENT_MOVEMENT_REASON)
    ) {
      return mappedMovementReasons;
    }

    return [
      ...mappedMovementReasons,
      {
        value: REINSTATEMENT_MOVEMENT_REASON,
        label: REINSTATEMENT_MOVEMENT_REASON,
      },
    ];
  }, [isDepartedVesselCall, movementReasons]);

  const movementReasonValue = formData.watch("movementReason");

  const handleMovementReasonChange = useCallback(
    (name: keyof MovementServiceFormData, value: string) => {
      if (name === "movementReason" && movementReasonValue !== value) {
        formData.setValue("associatedPortServiceId", "", {
          shouldDirty: true,
          shouldValidate: true,
        });
        updateFormData("associatedPortServiceId", "");
      }

      updateFormData(name, value);
    },
    [formData, movementReasonValue, updateFormData],
  );

  useEffect(() => {
    if (!isDepartedVesselCall || movementReasonValue === REINSTATEMENT_MOVEMENT_REASON) {
      return;
    }

    formData.setValue("movementReason", REINSTATEMENT_MOVEMENT_REASON, {
      shouldDirty: false,
      shouldValidate: true,
    });
    updateFormData("movementReason", REINSTATEMENT_MOVEMENT_REASON);
  }, [formData, isDepartedVesselCall, movementReasonValue, updateFormData]);

  const { data: associatedPortService = [] } = useQuery({
    queryKey: ["getAssociatedPortServiceDetailsById", vesselCallId, movementReasonValue],
    queryFn: () => {
      return getAssociatedPortServiceDetailsById({
        vesselCallId,
        movementReason:
          movementReasonValue === "ShipToShip"
            ? "STS"
            : movementReasonValue === "Bunkering"
              ? "Bunkering"
              : "",
        service: "bff-vessel-call",
      });
    },
  });

  const associatedPortServiceOptions = useMemo(
    () => mapAssociatedPortServiceToOptions(associatedPortService),
    [associatedPortService],
  );

  const movementType = useMovementServiceFormStore((state) => state.movementType);
  const fromLocation = useMovementServiceFormStore((state) => state.from);
  const toLocation = useMovementServiceFormStore((state) => state.to);
  const showRadioButtons = shouldShowVesselDetailRadioButtons({
    movementType,
    fromLocation,
    toLocation,
  });
  const shouldDisableDraftFields = fromLocation === "SEA" && !hasClosedSeaPastMovement;

  const showAssociatedPortService =
    movementReasonValue === "ShipToShip" || movementReasonValue === "Bunkering";

  useEffect(() => {
    if (!showAssociatedPortService) {
      formData.clearErrors("associatedPortServiceId");
      return;
    }
    if (associatedPortService.length === 0) {
      formData.setError("associatedPortServiceId", {
        type: "manual",
        message: t(`${LANG_BASE}.errors.no-associated-port-service`),
      });
    } else {
      formData.clearErrors("associatedPortServiceId");
    }
  }, [showAssociatedPortService, associatedPortService.length]);

  const yesNoOptions = [
    { value: true, label: t("common.yes") },
    { value: false, label: t("common.no") },
  ];

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${LANG_BASE}.sections.vessel-information`)}>
        <FormItem<MovementServiceFormData, "vesselCondition">
          name="vesselCondition"
          type="autocomplete"
          label={t(`${LANG_BASE}.fields.vessel-condition`)}
          testId="movement-vessel-condition"
          options={vesselConditionOptions}
          onFieldChange={updateFormData}
          required
        />
        <FormItem<MovementServiceFormData, "movementReason">
          name="movementReason"
          type="autocomplete"
          label={t(`${LANG_BASE}.fields.movement-reason`)}
          testId="movement-reason"
          options={movementReasonOptions}
          disabled={isDepartedVesselCall}
          onFieldChange={handleMovementReasonChange}
          required
        />
        <FormItem<MovementServiceFormData, "draftFore">
          name="draftFore"
          type="number"
          label={t(`${LANG_BASE}.fields.draft-fore`)}
          testId="movement-draft-fore"
          disabled={shouldDisableDraftFields}
          onFieldChange={updateFormData}
          maxDigits={5}
          decimals={3}
          canBeginWithZero={false}
        />
        <FormItem<MovementServiceFormData, "draftAft">
          name="draftAft"
          type="number"
          label={t(`${LANG_BASE}.fields.draft-aft`)}
          testId="movement-draft-aft"
          disabled={shouldDisableDraftFields}
          onFieldChange={updateFormData}
          maxDigits={5}
          decimals={3}
          canBeginWithZero={false}
        />
        {showAssociatedPortService && (
          <FormItem<MovementServiceFormData, "associatedPortServiceId">
            name="associatedPortServiceId"
            type="autocomplete"
            label={t(`${LANG_BASE}.fields.associated-port-service`)}
            testId="associated-port-service"
            options={associatedPortServiceOptions}
            onFieldChange={updateFormData}
          />
        )}
        {showRadioButtons && (
          <>
            <FormItem<MovementServiceFormData, "hasProtrusionsOrOverhand">
              name="hasProtrusionsOrOverhand"
              type="radio"
              label={t(`${LANG_BASE}.fields.has-protrusions`)}
              testId="movement-protrusions"
              options={yesNoOptions}
              horizontalRadioButtons
              inlineRadioButtons={false}
              onFieldChange={updateFormData}
            />
            <FormItem<MovementServiceFormData, "hasOilStains">
              name="hasOilStains"
              type="radio"
              label={t(`${LANG_BASE}.fields.has-oil-stains`)}
              testId="movement-oil-stains"
              options={yesNoOptions}
              horizontalRadioButtons
              inlineRadioButtons={false}
              onFieldChange={updateFormData}
            />
          </>
        )}
      </FormSection>
    </FormProvider>
  );
};

export default MovementVesselDetails;
